/**
 * S5-04 review queue: the Draft objects per pack with their PRS 8.2 metadata,
 * the GOV-07 AI-authored marker and the GOV-09 test-only badge, with the
 * approval and publication actions bound to (object, content version).
 *
 * The buttons are greyed from can_approve / can_publish, which migration 262
 * computes from the guard's own conditions. That is a convenience and NOT the
 * enforcement: pressing a button that should not have been enabled still ends
 * at the migration 237 guard, and its named refusal is what the screen shows.
 */
import React, { useState } from 'react';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import {
  DEVELOPMENT_LEVEL_LABELS,
  groupQueueByPack,
  type ReviewQueueRow,
} from '../../../lib/elevateGovernance';
import { AiAuthoredBadge, GuardRefusal, LifecycleBadge, TestOnlyBadge } from './ContentBadges';

interface Props {
  rows: ReviewQueueRow[];
  /** GOV-02 / DEC-09. False disables both actions and explains why. */
  isApprover: boolean;
  busyObjectUuid: string | null;
  /** Keyed by object_uuid; the guard's message, verbatim. */
  refusals: Record<string, string>;
  /** Keyed by object_uuid; the success line the action returned. */
  outcomes: Record<string, string>;
  onApprove: (row: ReviewQueueRow) => void;
  onPublish: (row: ReviewQueueRow) => void;
}

export const ContentReviewQueue: React.FC<Props> = ({
  rows,
  isApprover,
  busyObjectUuid,
  refusals,
  outcomes,
  onApprove,
  onPublish,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const packs = groupQueueByPack(rows);

  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-gray-500">
        No learning objects match this filter.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {packs.map((pack) => (
        <section key={pack.contentPackId} className="bg-white shadow-sm rounded-lg border border-gray-200">
          <header className="px-4 py-3 border-b border-gray-200 flex items-baseline justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{pack.packTitle}</h3>
              <p className="text-xs text-gray-500 font-mono">
                {pack.contentPackId} · authored {pack.authoredCreatedAt}
              </p>
            </div>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {pack.objects.length} object{pack.objects.length === 1 ? '' : 's'}
            </span>
          </header>

          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Object</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Competency</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approval evidence</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pack.objects.map((row) => {
                const busy = busyObjectUuid === row.object_uuid;
                const isOpen = expanded === row.object_uuid;
                const refusal = refusals[row.object_uuid];
                const outcome = outcomes[row.object_uuid];
                return (
                  <React.Fragment key={row.object_uuid}>
                    <tr className="hover:bg-gray-50 align-top">
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : row.object_uuid)}
                          className="text-left"
                        >
                          <span className="text-xs font-medium text-gray-900">{row.title}</span>
                          <span className="block text-xs text-gray-500 font-mono">{row.object_id}</span>
                        </button>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {row.test_only && <TestOnlyBadge />}
                          {row.ai_authored && <AiAuthoredBadge />}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-300">
                            {row.object_type}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900">{row.competency_code}</td>
                      <td className="px-2 py-2 text-xs text-gray-900">
                        {DEVELOPMENT_LEVEL_LABELS[row.development_level] ?? row.development_level}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900 font-mono">{row.version}</td>
                      <td className="px-2 py-2">
                        <LifecycleBadge status={row.status} />
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-700">
                        {row.has_current_approval ? (
                          <>
                            <span className="block">{row.approver_name}</span>
                            <span className="block text-gray-500">{row.approver_email}</span>
                            <span className="block text-gray-500">
                              v{row.approved_version} · {row.approved_at}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-400">No approval for v{row.version}</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => onApprove(row)}
                            disabled={!isApprover || !row.can_approve || busy}
                            title={
                              isApprover
                                ? 'Record the GOV-02 approval bound to this content version'
                                : 'You do not hold the WingMentor-approver role (GOV-02, DEC-09)'
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => onPublish(row)}
                            disabled={!isApprover || !row.can_publish || busy}
                            title={
                              isApprover
                                ? 'Approved to Published, behind the migration 237 guard'
                                : 'You do not hold the WingMentor-approver role (GOV-02, DEC-09)'
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Publish
                          </button>
                        </div>
                      </td>
                    </tr>

                    {(refusal || outcome || isOpen) && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-3 py-2 space-y-2">
                          {refusal && <GuardRefusal message={refusal} />}
                          {outcome && (
                            <div className="px-3 py-2 rounded border border-emerald-300 bg-emerald-50 text-xs text-emerald-900 font-mono">
                              {outcome}
                            </div>
                          )}
                          {isOpen && (
                            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-700">
                              <div>
                                <dt className="text-gray-500">Observable behaviours</dt>
                                <dd className="font-mono">{row.observable_behaviour_codes.join(', ')}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">KSA dimension</dt>
                                <dd>{row.ksa_dimension}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Format</dt>
                                <dd>{row.format}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Duration</dt>
                                <dd>{row.duration_minutes} min</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Owner / author</dt>
                                <dd>
                                  {row.owner} / {row.author}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Language</dt>
                                <dd>{row.language}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Mandatory</dt>
                                <dd>{row.mandatory ? 'yes' : 'no'}</dd>
                              </div>
                              <div className="col-span-2 md:col-span-4">
                                <dt className="text-gray-500">Description</dt>
                                <dd>{row.description}</dd>
                              </div>
                            </dl>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
};

export default ContentReviewQueue;
